import {asIssueFieldSelection, type IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {type IssueField, IssueFieldPicker} from '@github-ui/item-picker/IssueFieldPicker'
import {useCallback, useMemo} from 'react'

import {AddIssueFieldButton} from './AddIssueFieldButton'

type FieldSelectorProps = {
  owner: string
  fieldsSet: string[]
  onIssueFieldSelected: (selectedField: IssueFieldSelection) => void
  shortcutEnabled: boolean
  markInteractionStart: () => void
  /// True if AddIssueFieldButton should appear inactive with alert icon
  inactive?: boolean
  /// True if AddIssueFieldButton should not be actionable
  disabled?: boolean
  /// Callback to receive the button element for focus management
  onButtonElementReceived?: (element: HTMLButtonElement | null) => void
}

export function FieldsSelector({
  owner,
  fieldsSet,
  onIssueFieldSelected,
  shortcutEnabled,
  markInteractionStart,
  inactive = false,
  disabled = false,
  onButtonElementReceived,
}: FieldSelectorProps) {
  const anchorElement = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return (anchorProps: React.HTMLAttributes<HTMLElement>, ref?: React.RefObject<HTMLButtonElement>) => {
      if (ref?.current) {
        onButtonElementReceived?.(ref.current)
      }
      return (
        <AddIssueFieldButton
          anchorProps={anchorProps}
          ref={ref}
          onClick={markInteractionStart}
          inactive={inactive}
          disabled={disabled}
        />
      )
    }
  }, [markInteractionStart, inactive, disabled, onButtonElementReceived])

  const selectIssueField = useCallback(
    (selectedFields: IssueField[]) => {
      const field = selectedFields[0]
      if (!field) return

      onIssueFieldSelected(asIssueFieldSelection(field))
    },
    [onIssueFieldSelected],
  )

  return (
    <IssueFieldPicker
      owner={owner}
      fieldsSet={fieldsSet}
      onSelectionChange={selectIssueField}
      shortcutEnabled={shortcutEnabled}
      anchorElement={anchorElement}
      insidePortal={false}
    />
  )
}

try{ FieldsSelector.displayName ||= 'FieldsSelector' } catch {}